/**
 * TDS (Tax Deducted at Source) calculation.
 *
 * @example
 * calculateTDS({ amount: 100_000, type: "rent" });
 * // { tds: 10000, rate: 0.10, type: "rent", fiscalYear: "2081/82" }
 *
 * calculateTDS({ amount: 100_000, type: "service-vat-registered" });
 * // { tds: 1500, rate: 0.015, ... }
 */

import { getRates } from "./data/index.js";
import { calculateIncomeTax } from "./income-tax.js";
import type {
  FiscalYear,
  SalaryDeductions,
  TaxProfile,
  TdsCategory,
} from "./types.js";
import { ensureNonNegative, r2 } from "./util.js";

/** TDS calculation result. */
export interface TdsResult {
  readonly type: TdsCategory;
  readonly amount: number;
  readonly rate: number;
  readonly tds: number;
  readonly netToVendor: number;
  readonly fiscalYear: FiscalYear;
}

/** Options for `calculateTDS`. */
export interface TdsOptions {
  /** Gross payment amount before TDS deduction. */
  readonly amount: number;
  /** Payment category. */
  readonly type: TdsCategory;
  /** Fiscal year — defaults to current. */
  readonly fiscalYear?: FiscalYear;
}

/**
 * Compute TDS for a payment by category. Salary TDS uses the slab table —
 * call `calculateSalaryTDS` for that.
 */
export function calculateTDS(options: TdsOptions): TdsResult {
  const { amount, type, fiscalYear } = options;
  ensureNonNegative(amount, "calculateTDS");
  if (type === "salary") {
    throw new TypeError(
      "calculateTDS: salary uses slab-based withholding; use calculateSalaryTDS instead",
    );
  }
  const rates = getRates(fiscalYear);
  const rate = rates.tds[type];
  if (rate === undefined) {
    throw new TypeError(`calculateTDS: unknown TDS category "${type}"`);
  }
  const tds = r2(amount * rate);
  return {
    type,
    amount: r2(amount),
    rate,
    tds,
    netToVendor: r2(amount - tds),
    fiscalYear: rates.fiscalYear,
  };
}

/** Get the TDS rate for a category at a given FY. */
export function getTDSRate(type: TdsCategory, fiscalYear?: FiscalYear): number {
  const rates = getRates(fiscalYear);
  const rate = rates.tds[type];
  if (rate === undefined) {
    throw new TypeError(`getTDSRate: unknown TDS category "${type}"`);
  }
  return rate;
}

/** Salary TDS options. */
export interface SalaryTdsOptions {
  /** Annual gross salary (NPR). */
  readonly annualIncome: number;
  /** Filing status — defaults to `"single"`. */
  readonly status?: TaxProfile["status"];
  /** Tax-payer profile flags. */
  readonly profile?: Omit<TaxProfile, "status">;
  /**
   * Annual deductions to subtract from gross. Either:
   *  - a pre-calculated `number` total, or
   *  - a `SalaryDeductions` block (retirement / lifeInsurance / healthInsurance
   *    / donation) which is internally capped via `totalDeductions`.
   */
  readonly deductions?: number | SalaryDeductions;
  /** Fiscal year — defaults to current. */
  readonly fiscalYear?: FiscalYear;
}

/** Salary TDS result. */
export interface SalaryTdsResult {
  readonly fiscalYear: FiscalYear;
  readonly annualTax: number;
  readonly monthlyTds: number;
}

/**
 * Compute monthly salary TDS via the slab table. Splits annual tax into 12
 * equal monthly withholdings.
 *
 * For festival-bonus / 13-month payroll, use `calculatePayroll` which handles
 * proration explicitly.
 */
export function calculateSalaryTDS(options: SalaryTdsOptions): SalaryTdsResult {
  const deductionsTotal =
    typeof options.deductions === "number" || options.deductions === undefined
      ? options.deductions
      : totalDeductions(
          options.deductions,
          options.fiscalYear,
          options.profile?.isSSFMember ?? false,
          Math.max(0, options.annualIncome),
        );
  const result = calculateIncomeTax({
    income: options.annualIncome,
    status: options.status,
    fiscalYear: options.fiscalYear,
    profile: options.profile,
    deductions: deductionsTotal,
  });
  return {
    fiscalYear: result.fiscalYear,
    annualTax: result.tax,
    monthlyTds: r2(result.tax / 12),
  };
}

/** Convenience: compute total deductions from a `SalaryDeductions` block. */
export function totalDeductions(
  d: SalaryDeductions,
  fiscalYear?: FiscalYear,
  isSSFMember = false,
  taxableIncomeBeforeDonation?: number,
): number {
  const rates = getRates(fiscalYear);
  const caps = rates.deductionCaps;

  const retCap = isSSFMember ? caps.retirementSsf : caps.retirement;
  const retirement = Math.min(d.retirement ?? 0, retCap);
  const lifeInsurance = Math.min(d.lifeInsurance ?? 0, caps.lifeInsurance);
  const healthInsurance = Math.min(d.healthInsurance ?? 0, caps.healthInsurance);

  let donation = 0;
  if (d.donation && d.donation > 0) {
    let cap = caps.donation;
    if (taxableIncomeBeforeDonation !== undefined) {
      cap = Math.min(cap, caps.donationPercentOfTaxableIncome * taxableIncomeBeforeDonation);
    }
    donation = Math.min(d.donation, cap);
  }

  return r2(retirement + lifeInsurance + healthInsurance + donation);
}
